import { createStore } from 'jotai';

/**
 * Creates a new Jotai store instance.
 *
 * This follows the same pattern as getQueryClient() for TanStack Query.
 * On the server, we always create a new store to prevent memory leaks
 * and state bleeding between requests. On the client, we reuse a singleton.
 *
 * WHY THIS IS NEEDED:
 * When using Jotai with Next.js SSR without a custom store, Jotai uses its
 * global default store. This global store persists across ALL server requests,
 * causing two critical issues:
 *
 * 1. Memory Leak: Atoms and their values accumulate in the global store
 *    across requests, never being garbage collected since the store
 *    reference is held indefinitely.
 *
 * 2. State Bleeding: Different users' requests could potentially share
 *    state through the global store, causing data leakage.
 *
 * By creating a new store per server request, we ensure proper isolation
 * and allow garbage collection after each request completes.
 */
function makeJotaiStore() {
  return createStore();
}

let browserJotaiStore: ReturnType<typeof createStore> | undefined;

export function getJotaiStore() {
  // Server: always create a new store per request
  if (typeof window === 'undefined') {
    return makeJotaiStore();
  }
  // Browser: use a singleton
  if (!browserJotaiStore) {
    browserJotaiStore = makeJotaiStore();
  }
  return browserJotaiStore;
}
